import { useEffect, useState } from 'react';
import { api, type WageOverrideRow } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

const WAGE_LABEL: Record<string, string> = {
  open: 'open-shop', ca_prevailing: 'CA prevailing (DIR)', nv_prevailing: 'NV prevailing', davis_bacon: 'Davis-Bacon',
};

// Per-bid wage rates (estimator self-service). Every bid starts from the company
// rate library; the estimator can override the loaded $/hr for THIS bid only.
export function WageRatesCard({ projectId }: { projectId: string }) {
  const { user } = useAuth();
  const { t } = useLang();
  const isAdmin = user?.role === 'admin';
  const [rates, setRates] = useState<WageOverrideRow[]>([]);
  const [wageType, setWageType] = useState('open');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  function load() {
    api.getWageOverrides(projectId).then((r) => { setRates(r.rates); setWageType(r.wage_type); }).catch(() => {});
  }
  useEffect(() => { load(); }, [projectId]);

  async function save(category: string, val: string) {
    setBusy(category);
    try {
      const res = await api.setWageOverride(projectId, { category, hourly: val.trim() === '' ? null : Number(val) });
      setMsg(res.floored ? `${category.replace(/_/g, ' ')}: ${t({ en: "below the company rate isn't allowed — kept at the default.", fil: 'hindi pwedeng mas mababa sa company rate — kept at the default.' })}` : '');
      load();
    }
    finally { setBusy(''); }
  }

  if (!rates.length) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>{t({ en: 'Labor rates — this bid', fil: 'Labor rates — ang bid na ito' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'starts from the company', fil: 'nagsisimula sa company' })} {WAGE_LABEL[wageType] ?? wageType} {t({ en: 'rates; edit any rate for this bid only', fil: 'rates; i-edit ang anumang rate para sa bid na ito lang' })}</span></h2>
      {msg && <div className="banner banner-warning" style={{ marginTop: 6, fontSize: 13 }}>⚠️ {msg}</div>}
      <table style={{ marginTop: 6 }}>
        <thead><tr><th>{t({ en: 'Classification', fil: 'Classification' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Company default $/hr', fil: 'Company default $/hr' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'This bid $/hr', fil: 'Bid na ito $/hr' })}</th><th></th></tr></thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.category}>
              <td><strong>{r.classification}</strong>{r.is_sample && r.override_hourly == null && <span style={{ color: 'var(--warning)', fontSize: 11 }}> ⚠ {t({ en: 'sample', fil: 'sample' })}</span>}</td>
              <td style={{ textAlign: 'right' }} className="muted">${r.default_hourly.toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>
                <input key={`${r.category}-${r.override_hourly ?? 'def'}`} defaultValue={r.override_hourly ?? ''} placeholder={r.default_hourly.toFixed(2)}
                  disabled={busy === r.category} onBlur={(e) => { if (e.target.value !== String(r.override_hourly ?? '')) save(r.category, e.target.value); }}
                  style={{ width: 110, textAlign: 'right' }} />
              </td>
              <td style={{ width: 90 }}>
                {r.override_hourly != null
                  ? <button className="btn btn-ghost btn-sm" disabled={busy === r.category} onClick={() => save(r.category, '')}>↩ {t(G.reset)}</button>
                  : <span className="muted" style={{ fontSize: 11 }}>{t({ en: 'default', fil: 'default' })}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
        {t({ en: 'Loaded straight-time rate (base + burden + fringe). Shift and crew-mix multipliers still apply on top. Leave a box blank to use the company default.', fil: 'Loaded straight-time rate (base + burden + fringe). Naka-apply pa rin sa ibabaw ang shift at crew-mix multipliers. Iwanang blangko ang isang box para gamitin ang company default.' })}{!isAdmin && t({ en: ' You can raise a rate but not set it below the company default.', fil: ' Pwede mong itaas ang rate pero hindi pwedeng mas mababa sa company default.' })} <strong>{t({ en: 'This only changes THIS bid — the company rate library is untouched. Re-generate the bid to apply.', fil: 'Binabago lang nito ang BID na ito — hindi nagagalaw ang company rate library. I-re-generate ang bid para mag-apply.' })}</strong>
      </p>
    </div>
  );
}
